import {
  findSticker,
  findStickerBySlug,
  createUserInvite,
  findPublishedStickers,
  findPublishedStickersWithFiltering,
  findPurchasedStickers,
  checkUserExists,
  benevityCauses,
  getHomeScreenAppData,
  organization,
  findAllOrganizations,
  findOrganizationsV2,
  findPublishedStickersShortDetails,
  findOrgStickers,
  findApprovedOrgStickers,
  findApprovedOrgStickersBySlug,
} from '@/graphs/gql';
import { signUp } from '@/graphs/invite';
import { createStickerWebIntent, curatedBenevityCauses, createStickerWebIntentPublic } from '@/graphs/payment';
import { isStickerPurchased } from '@/graphs/stickers';
import globalShared from '@/shared';
import { getProfile, userPublicProfile, userPublicProfileBySlug } from '@/graphs/profile';
import { organizationPublicProfile, organizationPublicProfileBySlug } from '@/graphs/organization';

export default function commonShared(): { [key: string]: any } {
  const {
    ref,
    route,
    router,
    removeItemGetConfirm,
    makeGraphRequest,
    getAuthToken,
    preSignedUrl,
  } = globalShared();

  async function getPublicSticker(id: number): Promise<any> {
    const response = await makeGraphRequest(
      'query',
      findSticker,
      { id },
      null,
    );
    return response.data.findSticker;
  }

  async function getSticker(id: number): Promise<any> {
    const response = await makeGraphRequest(
      'query', findSticker, { id }
    );
    return response.data.findSticker;
  }

  async function getStickerBySlug(slug: string): Promise<any> {
    const response = await makeGraphRequest(
      'query', findStickerBySlug, { slug }
    );
    return response.data.findStickerBySlug;
  }

  async function getPublishedStickers(take: number = 10, skip: number = 0, filter: string[] = [], searchString: string = ''): Promise<any> {
    const response = await makeGraphRequest(
      'query', findPublishedStickers, { filter, take, skip, searchString }, getAuthToken(),
    );
    return response.data.findPublishedStickers;
  }

  async function getPublishedPublicStickers(take: number = 10, skip: number = 0, filter: string[] = [], searchString: string = ''): Promise<any> {
    const response = await makeGraphRequest(
      'query', findPublishedStickersShortDetails, { take, skip, orderAsc: false, searchString },
    );
    return response.data.findPublishedStickersShortDetails;
  }

  async function getPublishedStickersWithFiltering(filter: any, take: number = 10, skip: number = 0, searchString: string = ''): Promise<any> {
    const response = await makeGraphRequest(
      'query', findPublishedStickersWithFiltering, { filter, take, skip, searchString }, getAuthToken(),
    );
    return response.data.findPublishedStickers;
  }

  async function getPurchasedStickers(entity: string, entityId: string): Promise<any> {
    const response = await makeGraphRequest(
      'query', findPurchasedStickers, { entity, entityId }, getAuthToken(),
    );
    return response.data.findPurchasedStickers;
  }

  async function doCreateUserInvite(input: any): Promise<any> {
    const response = await makeGraphRequest(
      'mutation',
      createUserInvite,
      { input },
      null,
    );
    return response.data.createUserInvite;
  }

  async function doSignUp(input: any): Promise<any> {
    const response = await makeGraphRequest(
      'mutation',
      signUp,
      { input },
      null,
    );
    return response.data.signUp;
  }

  type CreateStickerWebIntentVariables = {
    stickerId: string,
    causeId: string,
    cost?: string,
  }

  async function doCreateStickerWebIntent(stickerId: string, causeId: string, customAmount?: number | string): Promise<any> {
    const variables: CreateStickerWebIntentVariables = { stickerId, causeId }

    if (customAmount !== undefined) {
      variables.cost = customAmount.toString();
    }

    const response = await makeGraphRequest(
      'mutation',
      createStickerWebIntent,
      variables,
      getAuthToken(),
    );
    return response.data.createStickerWebIntent;
  }
  async function doCreateStickerWebIntentPublic(stickerId: string, causeId: string, customAmount?: number | string): Promise<any> {
    const variables: CreateStickerWebIntentVariables = { stickerId, causeId };

    if (customAmount !== undefined) {
      variables.cost = customAmount.toString();
    }

    const response = await makeGraphRequest(
      'mutation',
      createStickerWebIntentPublic,
      variables,
    );
    return response.data.createStickerWebIntentPublic;
  }


  // eslint-disable-next-line max-len
  async function checkIsStickerPurchased(entity: string, entityId: string, stickerId: string): Promise<any> {
    const response = await makeGraphRequest(
      'query', isStickerPurchased, { entity, entityId, stickerId }, getAuthToken(),
    );
    return response.data.isStickerPurchased;
  }

  async function doCheckUserExists(phone: string, email: string, userId: string): Promise<any> {
    const response = await makeGraphRequest(
      'query', checkUserExists, { phone, email, userId }, null,
    );
    return response;
  }

  async function getCuratedBenevityCauses(): Promise<any> {
    const response = await makeGraphRequest(
      'query', curatedBenevityCauses, {}, null,
    );
    return response.data.curatedBenevityCauses;
  }

  async function getcausesAsDropdown(query: string): Promise<Array<Map<string, any>>> {
    const response = await makeGraphRequest(
      'query', benevityCauses, { q: query, page: '1' }, getAuthToken(),
    );
    return response.data.benevityCauses.causes;
  }

  async function fetchHomeScreenAppData(): Promise<any> {
    const response = await makeGraphRequest(
      'query', getHomeScreenAppData, {}, null,
    );
    return response.data.getHomeScreenAppData;
  }

  async function fetchProfile(): Promise<any> {
    const token = getAuthToken();
    if (!token) {
      router.push('/auth/signin');
      return null;
    }

    const response = await makeGraphRequest(
      'query', getProfile, {}, token,
    );
    return response.data.profile;
  }

  async function fetchPublicProfile(userId: string): Promise<any> {
    const response = await makeGraphRequest(
      'query', userPublicProfile, { id: userId },
    );
    return response.data.userPublicProfile;
  }

  async function fetchPublicProfileBySlug(slug: string): Promise<any> {
    const response = await makeGraphRequest(
      'query', userPublicProfileBySlug, { slug },
    );
    return response.data.userPublicProfileBySlug;
  }

  async function fetchOrganization(orgId: string): Promise<any> {
    const token = getAuthToken();
    if (!token) {
      router.push('/auth/signin');
      return null;
    }

    const response = await makeGraphRequest(
      'query', organization, { id: orgId }, token,
    );
    return response.data.getOrganization;
  }

  async function fetchPublicOrganization(orgId: string): Promise<any> {
    const response = await makeGraphRequest(
      'query', organizationPublicProfile, { id: orgId }
    );
    return response.data.organizationPublicProfile;
  }

  async function fetchPublicOrganizationBySlug(slug: string): Promise<any> {
    const response = await makeGraphRequest(
      'query', organizationPublicProfileBySlug, { slug }
    );
    return response.data.organizationPublicProfileBySlug;
  }

  async function fetchAllOrganizations(take = 20, skip = 0, searchString = ''): Promise<any> {
    const token = getAuthToken();
    if (!token) {
      router.push('/auth/signin');
      return null;
    }

    const baseInput = {
      ids: null,
      entity: null,
      entityIds: null,
      isActive: null,
      statuses: null,
      query: searchString || null,
      page: Math.floor(skip / take) + 1,
      pageSize: take
    };

    const response = await makeGraphRequest(
      'query', findOrganizationsV2, { baseInput }, token);
    return response.data.findOrganizationsV2;
  }

  async function getOrgStickers(orgId: string): Promise<any> {
    const response = await makeGraphRequest(
      'query', findOrgStickers, { orgId }, getAuthToken(),
    );
    return response.data.findOrgStickers;
  }

  async function fetchApprovedOrgStickers(orgId: string): Promise<any> {
    const response = await makeGraphRequest(
      'query', findApprovedOrgStickers, { orgId }, null,
    );
    return response.data.findApprovedOrgStickers;
  }

  async function fetchApprovedOrgStickersBySlug(slug: string): Promise<any> {
    const response = await makeGraphRequest(
      'query', findApprovedOrgStickersBySlug, { slug }, null,
    );
    return response.data.findApprovedOrgStickersBySlug;
  }

  return {
    ref,
    route,
    router,
    loading: ref(false),
    dialog: ref(false),
    removeItemGetConfirm,
    makeGraphRequest,
    preSignedUrl,

    getSticker,
    getStickerBySlug,
    doCreateUserInvite,
    getPublishedStickers,
    getPublishedStickersWithFiltering,
    getPurchasedStickers,
    doCreateStickerWebIntent,
    doCreateStickerWebIntentPublic,
    checkIsStickerPurchased,
    getPublicSticker,
    doSignUp,
    doCheckUserExists,
    getCuratedBenevityCauses,
    getcausesAsDropdown,
    fetchHomeScreenAppData,
    fetchProfile,
    fetchPublicProfile,
    fetchPublicProfileBySlug,
    fetchOrganization,
    fetchPublicOrganization,
    fetchPublicOrganizationBySlug,
    fetchAllOrganizations,
    getPublishedPublicStickers,
    getOrgStickers,
    fetchApprovedOrgStickers,
    fetchApprovedOrgStickersBySlug,
  };
}
